import { useCallback } from "react";

import { CurrentOrganizationLogo } from "@/components/organization";
import { CurrentPlatformLogo } from "@/components/platform";
import { prefetchNavLinkPage } from "@/routes/prefetch";

import { BaseNavLink, BaseNavLinkAction } from "./BaseNavLink";
import { NavLinkItem } from "./NavLink.types";
import { NavLinkIcon } from "./NavLinkIcon";
import { useGetNavLinkLabel } from "./useGetNavLinkLabel";
import { useGetNavLinkPath } from "./useGetNavLinkPath";
import { useGetNavLinkSubtitle } from "./useGetNavLinkSubtitle";
import { useIsNavLinkActive } from "./useIsNavLinkActive";

export type NavLinkProps = {
  id?: string;
  item: NavLinkItem;
  actions: BaseNavLinkAction[];
  className?: string;
};

export const NavLink = ({ id, item, actions, className }: NavLinkProps) => {
  const navId = id ?? `nav-${item.type}`;
  const getNavLinkLabel = useGetNavLinkLabel();
  const getNavLinkSubtitle = useGetNavLinkSubtitle();
  const getNavLinkPath = useGetNavLinkPath();
  const isActive = useIsNavLinkActive();

  const handlePrefetch = useCallback(() => {
    prefetchNavLinkPage(item.type);
  }, [item.type]);

  const wrap = (child: React.ReactNode) => (
    <div className="contents" onPointerEnter={handlePrefetch} onFocus={handlePrefetch}>
      {child}
    </div>
  );

  switch (item.type) {
    case "platformSettings":
      return wrap(
        <BaseNavLink
          id={navId}
          href={getNavLinkPath(item.type)}
          title={getNavLinkLabel(item.type)}
          subtitle={getNavLinkSubtitle(item.type)}
          className={className}
          renderStartIcon={(props) => <NavLinkIcon type={item.type} {...props} />}
          renderEndIcon={(props) => <CurrentPlatformLogo {...props} />}
          actions={actions}
          isActive={isActive(item)}
        />
      );

    case "organizationSettings":
      return wrap(
        <BaseNavLink
          id={navId}
          href={getNavLinkPath(item.type)}
          subtitle={getNavLinkSubtitle(item.type)}
          title={getNavLinkLabel(item.type)}
          className={className}
          renderStartIcon={(props) => <NavLinkIcon type={item.type} {...props} />}
          renderEndIcon={(props) => <CurrentOrganizationLogo {...props} />}
          actions={actions}
          isActive={isActive(item)}
        />
      );

    default:
      return wrap(
        <BaseNavLink
          id={navId}
          href={getNavLinkPath(item.type)}
          subtitle={getNavLinkSubtitle(item.type)}
          title={getNavLinkLabel(item.type)}
          className={className}
          renderStartIcon={(props) => <NavLinkIcon type={item.type} {...props} />}
          actions={actions}
          isActive={isActive(item)}
        />
      );
  }
};
